import { FluidTheme, FluidThemePalette } from '@lmig/fluid-core';
import {
  Component,
  ComponentInterface,
  Element,
  h,
  Host,
  Method,
  Prop,
  Watch,
} from '@stencil/core';
import { BehaviorSubject } from 'rxjs';

import { ElementRegistry } from '../../utils/elements/element-registry.utils';

/**
 * @displayName Theme Provider
 */
@Component({
  tag: 'fluid-theme-provider',
  styleUrl: 'fluid-theme-provider.scss',
  shadow: true,
})
export class FluidThemeProvider implements ComponentInterface {
  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element
   */
  @Element() host: HTMLFluidThemeProviderElement;

  // ================================================================ //
  // -- Props

  /**
   * Set the global theme for the components contained within.
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * Private observable containing the current global theme. We use
   * a behaviour subject so theme can be grabbed immediately by the
   * first observer, without having to wait for an update.
   */
  private _theme$: BehaviorSubject<string> = new BehaviorSubject(
    this.theme || FluidTheme.CORP
  );

  /**
   * When provided, sets the current theme's color palette & appearance.
   * NOTE: a theme palette acts as a "style override" applied ON TOP of
   * the current theme & isn't necessary for "standard" theme aesthetics.
   */
  @Prop({ mutable: true }) themePalette: FluidThemePalette;

  @Watch('theme')
  @Watch('themePalette')
  setTheme() {
    this._theme$.next(this.theme);
    ElementRegistry.setThemeProvider(this.host, {
      theme: this.theme,
      themePalette: this.themePalette,
    });
  }

  /**
   * CSS z-index value that will be used to set z-index values for all other
   * sticky / absolutely positioned components. All component z-index values
   * will be at or above this number.
   */
  @Prop({ mutable: true }) baseZIndex: number;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   */
  connectedCallback() {
    ElementRegistry.setThemeProvider(this.host, {
      theme: this.theme,
      themePalette: this.themePalette,
    });
  }

  // ================================================================ //
  // -- Public Methods

  /**
   * Expose as public method to allow components to call it on a standard
   * DOM instance of the content block.
   */
  @Method() async getTheme() {
    return this._theme$;
  }

  /**
   * Public method that returns the global z-index threshold value.
   */
  @Method() async getBaseZIndex() {
    return this.baseZIndex;
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <Host>
        <slot />
      </Host>
    );
  }
}
